import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, CheckCircle, AlertCircle, Send, Calendar, Clock, Video } from '../components/icons';
import { NLPLogo } from '../components/nlp/NLPLogo';
import { supabase } from '../lib/supabase';

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'New to NLP / personal development' },
  { value: 'intermediate', label: 'Some experience with NLP or coaching' },
  { value: 'advanced', label: 'Experienced practitioner' },
  { value: 'practitioner', label: 'Certified NLP practitioner or coach' },
];

export function MasterclassRegistrationPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('beginner');
  const [questions, setQuestions] = useState('');
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);

    if (!fullName.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);

    try {
      const { error: insertError } = await supabase
        .from('masterclass_registrations')
        .insert({
          full_name: fullName.trim(),
          email: email.trim(),
          phone: phone.trim() || null,
          experience_level: experienceLevel,
          questions: questions.trim() || null,
          consent_marketing: consentMarketing,
          event_slug: 'thought-emotion-effort',
        });

      if (insertError) throw insertError;

      setSubmitted(true);
    } catch (err) {
      console.error('Error registering for masterclass:', err);
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFullName('');
    setEmail('');
    setPhone('');
    setExperienceLevel('beginner');
    setQuestions('');
    setConsentMarketing(false);
    setSubmitted(false);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-nlp-cream relative overflow-hidden" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      {/* Ambient glow */}
      <motion.div
        className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(92,122,74,0.10) 0%, transparent 70%)' }}
        animate={{ x: [0, -30, 0], y: [0, 20, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(201,169,97,0.10) 0%, transparent 70%)' }}
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 pt-8 pb-6 max-w-[1216px] mx-auto">
        <NLPLogo className="h-12" />
        <div className="hidden md:flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-nlp-cream-dark shadow-soft">
          <Sparkles className="w-3.5 h-3.5 text-nlp-gold-dark" />
          <span className="text-xs tracking-[0.2em] uppercase text-nlp-forest font-semibold">Free Masterclass</span>
        </div>
      </header>

      {/* Hero section */}
      <div className="relative z-10 max-w-[800px] mx-auto px-6 pt-4 pb-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6 border border-nlp-cream-dark shadow-soft">
            <span className="text-sm font-semibold text-nlp-forest">Live Online Masterclass</span>
          </div>
          <h1
            className="text-nlp-forest leading-tight mb-4"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(32px, 4.5vw, 52px)',
              fontWeight: 700,
              letterSpacing: '-0.015em',
            }}
          >
            Thought, Emotion & Effort
          </h1>
          <p className="text-lg text-nlp-taupe mb-2 font-medium">
            The Real Science of the Mind
          </p>
          <p className="text-base text-nlp-taupe/80 max-w-2xl mx-auto leading-relaxed mb-6">
            Discover the hidden mechanics that determine how you think, feel, act — and respond to the challenges of life.
          </p>

          {/* Event details */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-nlp-cream-dark shadow-soft">
              <Calendar className="w-4 h-4 text-nlp-olive" />
              <span className="text-sm font-medium text-nlp-forest">Wednesday, 4 November 2026</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-nlp-cream-dark shadow-soft">
              <Clock className="w-4 h-4 text-nlp-olive" />
              <span className="text-sm font-medium text-nlp-forest">10:00 AM – 12:00 PM EST</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-nlp-cream-dark shadow-soft">
              <Video className="w-4 h-4 text-nlp-olive" />
              <span className="text-sm font-medium text-nlp-forest">Live via Zoom</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Content + Form */}
      <div className="relative z-10 max-w-[800px] mx-auto px-6 pb-12">
        {/* What you'll discover */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white rounded-[32px] shadow-xl p-8 md:p-10 border border-nlp-cream-dark mb-8"
        >
          <h2 className="text-xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            What You Will Discover
          </h2>
          <ul className="space-y-2.5 text-sm text-nlp-forest/80 leading-relaxed">
            {[
              'How thought becomes action through emotion and effort',
              'Why emotion acts as the bridge between intention and physical action',
              'The three essential elements of control: start, change and stop',
              'How the mind estimates the effort required to achieve something',
              'Why incorrect estimations produce frustration, anger, fear or withdrawal',
              'What happens when your efforts repeatedly encounter resistance',
              'Why past emotional experiences can become activated by present situations',
              'How old conclusions continue influencing current choices',
              'Why different people experience the same challenge very differently',
              'How accumulated charge can reduce clarity, perseverance and personal power',
              'Why releasing what obstructs you can restore freedom, energy and ability',
            ].map((point, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 pt-6 border-t border-nlp-cream-dark">
            <h3 className="text-lg font-bold text-nlp-forest mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              Beyond Positive Thinking
            </h3>
            <p className="text-sm text-nlp-forest/80 leading-relaxed mb-3">
              Most personal-development approaches begin with visible thoughts, beliefs, emotions and behaviours. This masterclass asks a deeper question: <em className="text-nlp-forest font-medium">What is producing those thoughts, emotions and reactions in the first place?</em>
            </p>
            <p className="text-sm text-nlp-forest/80 leading-relaxed">
              Rather than trying to motivate yourself over an unwanted reaction, you will explore the experiences, conclusions, resistance and emotional charge that may be operating beneath it. The aim is not to manufacture a temporary positive state — it is to understand what obstructs the individual so that natural clarity, resilience and self-determination can re-emerge.
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-nlp-cream-dark">
            <h3 className="text-lg font-bold text-nlp-forest mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              Discover the Sequence Behind Human Experience
            </h3>
            <div className="bg-nlp-cream/60 rounded-xl p-4 border border-nlp-cream-dark mb-3">
              <p className="text-sm font-semibold text-nlp-forest text-center">
                Being → Intention → Thought → Emotion → Effort → Action → Results
              </p>
            </div>
            <div className="bg-nlp-cream/60 rounded-xl p-4 border border-nlp-cream-dark">
              <p className="text-sm font-semibold text-nlp-forest text-center">
                Resistance → Emotional charge → Reactive conclusions → Reduced ability → Repeated patterns
              </p>
            </div>
            <p className="text-sm text-nlp-taupe mt-3 leading-relaxed">
              This provides a powerful framework for understanding why people can know exactly what they want — yet remain unable to translate that intention into consistent action.
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-nlp-cream-dark">
            <h3 className="text-lg font-bold text-nlp-forest mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              This Masterclass Is For You If You Want To
            </h3>
            <ul className="space-y-2 text-sm text-nlp-forest/80 leading-relaxed">
              {[
                'Understand human behaviour beneath its visible symptoms',
                'Discover why people become emotionally stuck',
                'Improve your ability to handle setbacks and resistance',
                'Understand the connection between emotional state and effectiveness',
                'Recognise the deeper source of recurring emotional patterns',
                'Add another dimension to your work in NLP, hypnosis or coaching',
                'Recover greater freedom, choice and perseverance in your own life',
              ].map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Registration form */}
        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-[32px] shadow-xl p-10 text-center border border-nlp-cream-dark"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-16 h-16 bg-nlp-olive/15 rounded-2xl flex items-center justify-center mx-auto mb-5"
              >
                <CheckCircle className="w-8 h-8 text-nlp-olive" />
              </motion.div>
              <h2 className="text-2xl font-bold text-nlp-forest mb-3">You're registered!</h2>
              <p className="text-nlp-taupe mb-6 max-w-md mx-auto">
                We've saved your spot for the Thought, Emotion & Effort masterclass on Wednesday, 4 November at 10:00 AM EST. You'll receive your private Zoom access link by email.
              </p>
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 bg-nlp-cream text-nlp-forest border border-nlp-cream-dark hover:bg-nlp-cream-dark rounded-full font-semibold transition-colors"
              >
                Register Another Person
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-white rounded-[32px] shadow-xl p-6 md:p-8 border border-nlp-cream-dark"
            >
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-nlp-forest mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                  Reserve Your Place
                </h2>
                <p className="text-sm text-nlp-taupe">
                  Enter your details below to register and receive your private access link.
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-5">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Full Name */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                  disabled={submitting}
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                  className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                  disabled={submitting}
                />
              </div>

              {/* Phone (optional) */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-nlp-forest mb-1.5">
                  Phone <span className="text-nlp-taupe font-normal">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                  disabled={submitting}
                />
              </div>

              {/* Experience level */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Your Experience Level</label>
                <select
                  value={experienceLevel}
                  onChange={(e) => setExperienceLevel(e.target.value)}
                  className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest cursor-pointer focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                  disabled={submitting}
                >
                  {EXPERIENCE_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </select>
              </div>

              {/* Questions (optional) */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-nlp-forest mb-1.5">
                  Questions for the Q&A <span className="text-nlp-taupe font-normal">(optional)</span>
                </label>
                <textarea
                  value={questions}
                  onChange={(e) => setQuestions(e.target.value)}
                  placeholder="Is there anything specific you'd like covered during the masterclass?"
                  rows={3}
                  className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors resize-none"
                  disabled={submitting}
                />
              </div>

              {/* Marketing consent */}
              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentMarketing}
                    onChange={(e) => setConsentMarketing(e.target.checked)}
                    disabled={submitting}
                    className="mt-1 w-5 h-5 rounded border-nlp-cream-dark text-nlp-olive focus:ring-nlp-olive/40 cursor-pointer"
                  />
                  <span className="text-sm text-nlp-forest/80 leading-relaxed">
                    I'd like to receive occasional updates about future events and resources from NLP OS.
                  </span>
                </label>
              </div>

              {/* Submit */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full font-semibold transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Register for the Free Masterclass
                  </>
                )}
              </motion.button>

              <p className="text-xs text-nlp-taupe text-center mt-4">
                Complimentary — no cost to attend. You'll receive a Zoom link by email.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <p className="relative z-10 text-xs tracking-[0.2em] uppercase text-nlp-taupe/40 text-center pb-8">
        my.nlpos.app
      </p>
    </div>
  );
}
